import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import NumberFormat from "react-number-format";

import {
  Col,
  DatePicker,
  Icon,
  Input,
  notification,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import ButtonStandard from "../../utils/button";
import { Container, Footer } from "./styles";

import useWindowSize from "../../../hooks/useWindowSize";
import { numberMask } from "../../../services/helpersMethodsService";
import { getIndexIntegrationsFarm } from "../../../services/integrationPartnersService";
import { findLatest } from "../../../services/picketSupplementSupplyService";
import {
  findPicketSupplyForecast,
  savePicketSupplyForecastAdjustments,
} from "../../../services/picketSupplyForecastService";
import { getSupplementIndexDropDown } from "../../../services/supplementService";

const DATE_FORMAT = "YYYY-MM-DD";

function treatKey(treat) {
  return `${treat.picketId}-${treat.supplyNumber}`;
}

const DrawerPicketSupplyForecast = ({ visible, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [forecastDate, setForecastDate] = useState(null);
  const [treats, setTreats] = useState([]);
  const [edits, setEdits] = useState({});
  const [supplements, setSupplements] = useState([]);
  const [isStarbrasEnabled, setIsStarbrasEnabled] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const size = useWindowSize();
  const texts = translation.picketSupplyForecast;

  const supplementOptions = useMemo(
    () =>
      isStarbrasEnabled
        ? supplements.filter((supplement) => supplement.supplementFormulated)
        : supplements,
    [supplements, isStarbrasEnabled],
  );

  const loadForecast = useCallback(
    async (date) => {
      setIsLoading(true);
      setEdits({});
      try {
        const {
          data: { results },
        } = await findPicketSupplyForecast({
          groupId,
          farmId,
          date: date.format(DATE_FORMAT),
        });
        setTreats(results.treats || []);
      } catch (error) {
        setTreats([]);
        if (error?.response?.data?.code === 5035) {
          notification.warn({ message: texts.messages.noSupply });
        } else {
          notification.error({ message: texts.messages.loadError });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, texts],
  );

  useEffect(() => {
    async function loadSupportData() {
      try {
        const {
          data: { results },
        } = await getSupplementIndexDropDown({ groupId, farmId });
        setSupplements(results || []);
      } catch (error) {
        setSupplements([]);
      }
      try {
        const {
          data: { results },
        } = await getIndexIntegrationsFarm({ groupId, farmId });
        setIsStarbrasEnabled(
          (results || []).some(
            (i) =>
              i.status === "Active" &&
              i.integrationPartner?.partnerSystemName === "StarBras",
          ),
        );
      } catch (error) {
        setIsStarbrasEnabled(false);
      }
    }

    async function loadInitialDate() {
      let date = moment().add(1, "days");
      try {
        const {
          data: { results },
        } = await findLatest({ groupId, farmId });
        if (results?.supplyDate) {
          const nextToLatest = moment(results.supplyDate).add(1, "days");
          if (!nextToLatest.isBefore(moment(), "day")) {
            date = nextToLatest;
          }
        }
      } catch (error) {}
      setForecastDate(date);
      loadForecast(date);
    }

    if (visible) {
      loadSupportData();
      loadInitialDate();
    } else {
      setTreats([]);
      setEdits({});
      setForecastDate(null);
    }
  }, [visible, groupId, farmId, loadForecast]);

  function handleChangeDate(date) {
    if (!date) return;
    setForecastDate(date);
    loadForecast(date);
  }

  function getValue(treat, field) {
    const edit = edits[treatKey(treat)];
    return edit && edit[field] !== undefined ? edit[field] : treat[field];
  }

  function handleEdit(treat, field, value) {
    const key = treatKey(treat);
    setEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  }

  function handleRestoreCalculated(treat) {
    const key = treatKey(treat);
    setEdits((prev) => ({
      ...prev,
      [key]: {
        supplementId: treat.calculatedSupplementId,
        quantity: treat.calculatedQuantity,
      },
    }));
  }

  function isSupplementAdjusted(treat) {
    return getValue(treat, "supplementId") !== treat.calculatedSupplementId;
  }

  function isQuantityAdjusted(treat) {
    return (
      Number(getValue(treat, "quantity")) !== Number(treat.calculatedQuantity)
    );
  }

  function isAdjusted(treat) {
    return isSupplementAdjusted(treat) || isQuantityAdjusted(treat);
  }

  function buildAdjustments() {
    return treats
      .filter((treat) => edits[treatKey(treat)] !== undefined)
      .filter(
        (treat) =>
          getValue(treat, "supplementId") !== treat.supplementId ||
          Number(getValue(treat, "quantity")) !== Number(treat.quantity),
      )
      .map((treat) => {
        const supplementId = getValue(treat, "supplementId");
        const quantity = getValue(treat, "quantity");
        return {
          picketId: treat.picketId,
          supplyNumber: treat.supplyNumber,
          supplementId:
            supplementId !== treat.calculatedSupplementId ? supplementId : null,
          quantity:
            quantity !== null &&
            quantity !== undefined &&
            Number(quantity) !== Number(treat.calculatedQuantity)
              ? quantity
              : null,
        };
      });
  }

  async function handleSave() {
    const body = buildAdjustments();
    if (body.length === 0) {
      notification.info({ message: texts.messages.nothingToSave });
      return;
    }
    setIsLoading(true);
    try {
      await savePicketSupplyForecastAdjustments({
        groupId,
        farmId,
        date: forecastDate.format(DATE_FORMAT),
        body,
      });
      notification.success({ message: texts.messages.saveSuccess });
      onClose();
    } catch (error) {
      notification.error({ message: texts.messages.saveError });
    } finally {
      setIsLoading(false);
    }
  }

  const rowSpanByKey = useMemo(() => {
    const spans = {};
    treats.forEach((treat, index) => {
      const isFirstOfPicket =
        index === 0 || treats[index - 1].picketId !== treat.picketId;
      spans[treatKey(treat)] = isFirstOfPicket
        ? treats.filter((t) => t.picketId === treat.picketId).length
        : 0;
    });
    return spans;
  }, [treats]);

  function renderPicketCell(content, treat) {
    return {
      children: content,
      props: { rowSpan: rowSpanByKey[treatKey(treat)] },
    };
  }

  const columns = [
    {
      title: texts.table.picket,
      dataIndex: "picketName",
      render: (text, treat) => renderPicketCell(text, treat),
    },
    {
      title: texts.table.lot,
      dataIndex: "lotName",
      render: (text, treat) => renderPicketCell(text, treat),
    },
    {
      title: texts.table.baseDayTotalSupplied,
      dataIndex: "baseDayTotalSupplied",
      render: (value, treat) =>
        renderPicketCell(`${numberMask(value || 0, false)} kg`, treat),
    },
    {
      title: texts.table.supplyNumber,
      dataIndex: "supplyNumber",
      render: (value, treat) =>
        treat.realized ? (
          <Tooltip title={texts.table.realizedTooltip}>
            <span>
              {value} <Icon type="check-circle" />
            </span>
          </Tooltip>
        ) : (
          value
        ),
    },
    {
      title: texts.table.recipe,
      dataIndex: "supplementId",
      render: (_, treat) => (
        <Select
          style={{ width: "100%", minWidth: 200 }}
          className={isSupplementAdjusted(treat) ? "adjustedValue" : ""}
          showSearch
          optionFilterProp="children"
          disabled={treat.realized}
          value={getValue(treat, "supplementId") || undefined}
          onChange={(value) => handleEdit(treat, "supplementId", value)}
        >
          {!supplementOptions.some(
            (s) => s.id === getValue(treat, "supplementId"),
          ) &&
            getValue(treat, "supplementId") && (
              <Select.Option value={getValue(treat, "supplementId")}>
                {getValue(treat, "supplementId") === treat.supplementId
                  ? treat.supplementName
                  : treat.calculatedSupplementName}
              </Select.Option>
            )}
          {supplementOptions.map((supplement) => (
            <Select.Option key={supplement.id} value={supplement.id}>
              {supplement.name}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: texts.table.calculatedQuantity,
      dataIndex: "calculatedQuantity",
      render: (value) => `${numberMask(value || 0, false)} kg`,
    },
    {
      title: texts.table.quantity,
      dataIndex: "quantity",
      render: (_, treat) => (
        <NumberFormat
          style={{ width: 140 }}
          customInput={Input}
          disabled={treat.realized}
          className={isQuantityAdjusted(treat) ? "adjustedValue" : ""}
          value={getValue(treat, "quantity") ?? ""}
          decimalScale={0}
          allowNegative={false}
          addonAfter="Kg"
          decimalSeparator=","
          thousandSeparator="."
          onValueChange={({ floatValue }) =>
            handleEdit(
              treat,
              "quantity",
              floatValue === undefined ? null : floatValue,
            )
          }
        />
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_, treat) =>
        !treat.realized &&
        isAdjusted(treat) && (
          <Tooltip title={texts.table.restoreCalculated}>
            <ButtonStandard
              type="button"
              buttonType="typeWithoutBackground"
              onClick={() => handleRestoreCalculated(treat)}
            >
              <Icon type="undo" />
            </ButtonStandard>
          </Tooltip>
        ),
    },
  ];

  return (
    <Container
      title={texts.title}
      width={size.width > 768 ? size.width * 0.8 : "100%"}
      onClose={onClose}
      maskClosable={false}
      visible={visible}
    >
      <Spin spinning={isLoading}>
        <div className="drawerForm">
          <Row type="flex" align="middle" gutter={8} className="rowFilters">
            <Col>
              <label>{texts.forecastDate}</label>
            </Col>
            <Col>
              <DatePicker
                format="DD/MM/YYYY"
                allowClear={false}
                value={forecastDate}
                disabledDate={(current) =>
                  current && current.isBefore(moment(), "day")
                }
                onChange={handleChangeDate}
              />
            </Col>
            <Col>
              <ButtonStandard
                type="button"
                buttonType="typeWithoutBackground"
                onClick={() => forecastDate && loadForecast(forecastDate)}
              >
                <Icon type="reload" />
              </ButtonStandard>
            </Col>
          </Row>
          <Table
            rowKey={(treat) => treatKey(treat)}
            dataSource={treats}
            columns={columns}
            pagination={false}
            scroll={{ x: true }}
            bordered
            size="small"
            locale={{ emptyText: texts.table.empty }}
          />
        </div>
        <Footer>
          <Row type="flex">
            <Col span={24} className="buttonsDiv">
              <ButtonStandard
                tabIndex="-1"
                type="button"
                buttonType="type7"
                onClick={onClose}
              >
                {translation.buttons.cancel}
              </ButtonStandard>
              <ButtonStandard
                tabIndex="-1"
                type="button"
                buttonType="type6"
                disabled={treats.length === 0}
                onClick={handleSave}
              >
                {translation.buttons.save}
              </ButtonStandard>
            </Col>
          </Row>
        </Footer>
      </Spin>
    </Container>
  );
};

export default DrawerPicketSupplyForecast;
